import {defineStore} from 'pinia'
import {AdminPermission, Club, hasMemberPermission, ModelType} from "@spoferan/spoferan-ts-core";
import type {ClubPermission} from "@spoferan/spoferan-ts-core";

/***********************************************************
 Vuex Club Store
 --------------------------
 Store to use for detail pages of a club.
 ************************************************************/

type ClubAuthPermissions = {is_initialized: boolean, can_edit: boolean, is_member: boolean, has_pending_invitation: boolean, has_pending_request: boolean, permissions: ClubPermission[]};

const defaultClubAuthPermissions = {
    is_initialized: false, // Whether the permissions were loaded
    can_edit: false,
    is_member: false,
    has_pending_invitation: false,
    has_pending_request: false,
    permissions: [],
} as ClubAuthPermissions;

export const useClubStore = defineStore('club', {
    state: () => ({
        /**
         * The current club details to show.
         */
        club: {} as Club,

        /**
         * The Stripe account details of the club.
         */
        account: null as {}|null,

        // Whether the modal to change the club's avatar is currently visible.
        avatarModal: false as boolean,

        /**
         * The permissions / relations of the currently authenticated user on the club.
         */
        authPermissions: {...defaultClubAuthPermissions} as ClubAuthPermissions,
    }),

    getters: {

        colorStyle: (state) => {
            const config = useRuntimeConfig();
            return state.club.color ?? config.public.colors.secondary;
        }
    },

    actions: {

        /**
         * Checks if the authenticated user has the specified permission.
         */
        hasMemberPermission(permission: ClubPermission) {
            const {$auth} = useNuxtApp();

            if (hasMemberPermission(this.club, permission)) {
                return true;
            }

            return $auth.user && $auth.user.is_admin && $auth.user.adminRole && $auth.user.adminRole.permissions && $auth.user.adminRole.permissions.indexOf(AdminPermission.ManageClubs) > -1
        },

        async refreshAuthPermissions() {
            const {$auth, $apiFetch} = useNuxtApp();

            // We know the permissions on client side for guests, so we can skip the request
            if (!$auth.loggedIn) {
                this.authPermissions = {...defaultClubAuthPermissions, is_initialized: true};
                return;
            }

            const route = useRoute();

            const {data} = await $apiFetch('/v1/auth/permissions', {
                params: {
                    model_type: ModelType.Club,
                    model_id: route.params.club, // Use route param to refresh auth permissions asap for page redirects
                }
            });
            this.authPermissions = {...data, is_initialized: true};
        },

        buildPageTitle(title: string): string {
            return title === this.club.display_name ? title : `${title} | ${this.club.display_name}`
        }
    }
});